import React, { useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import LoadingButton from "components/LoadingButton";
import ErrorMessage from "components/ErrorMessage";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import { changePasswordWithPasswordId } from "services/auth.service";
import { useLoginPage } from "hooks/useLoginPage";
import { isValidPassword } from "utils/validators";
import PasswordInput from "components/PasswordInput";
import { useText } from "context/TextProvider";

function ChangePassword(): JSX.Element {
  const text = useText();
  const { changeId } = useParams<{ changeId: string }>();
  const navigate = useNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!newPassword) {
      setError(text.forgotPassword.newPasswordRequired);
      return;
    }
    if (!isValidPassword(newPassword)) {
      setError(text.forgotPassword.passwordRequirements);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(text.forgotPassword.passwordsDoNotMatch);
      return;
    }
    setLoading(true);
    try {
      await changePasswordWithPasswordId({
        passwordId: changeId ?? "",
        password: newPassword,
        url: loginPageConfig?.urls?.submit_forgot_password_otp,
      });
      navigate(`/password-reset-success?config_id=${configId}`);
    } catch {
      setError(text.forgotPassword.resetFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta name="Change Password" />
      <PageTitle>{text.forgotPassword.title}</PageTitle>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <form onSubmit={handleChangePassword}>
        <PasswordInput
          id="newPassword"
          label={text.forgotPassword.newPasswordLabel}
          value={newPassword}
          placeholder={text.forgotPassword.newPasswordPlaceholder}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading}
        />
        {newPassword && !isValidPassword(newPassword) && <ErrorMessage>{text.forgotPassword.passwordRequirements}</ErrorMessage>}
        <PasswordInput
          id="confirmPassword"
          label={text.forgotPassword.confirmPasswordLabel}
          value={confirmPassword}
          placeholder={text.forgotPassword.confirmPasswordPlaceholder}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
        />

        <LoadingButton
          loading={loading}
          type="submit"
          disabled={loading}
          sx={{ width: "100%", height: "3.5rem", mb: "1rem" }}
        >
          {text.forgotPassword.resetPasswordButton}
        </LoadingButton>
      </form>

      <Box sx={{ textAlign: "center", fontSize: "0.875rem", color: "text.secondary", mb: "1.5rem" }}>
        <MuiLink color="secondary" component={RouterLink} to={`/login?config_id=${configId}`} replace sx={{ fontWeight: 500 }}>
          {text.forgotPassword.backToLoginLink}
        </MuiLink>
      </Box>
    </>
  );
}

export default ChangePassword;
